<script setup>
    import { ref, onMounted, watch, nextTick, getCurrentInstance, computed } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import { storeToRefs } from "pinia";
    import { usePageLoadingStore } from "../../../../stores/pageLoading";
    import { useUserStore } from "../../../user/stores/user";
    import SNBAdmin from '../../../../layouts/nav/SNBAdmin.vue'
    import SNBPartAdmin from "../../../../layouts/nav/SNBPartAdmin.vue";
    import curriculumService from "../../services/curriculum/curriculumService";
    import fileService from "../../../../services/fileService";
    import KSAInputWithBytes from "../../../../components/ui-components/input/KSAInputWithBytes.vue";
    import { useAlertStore } from "../../../../stores/alertStore";
    import _ from 'lodash'

    const { $number, $getByteLength } = getCurrentInstance().appContext.config.globalProperties;
    const { $alert, $confirm } = useAlertStore();

    const route = useRoute()
    const router = useRouter()
    const loadingStore = usePageLoadingStore()
    const userStore = useUserStore()
    const { isPartAdmin } = storeToRefs(userStore);
    const yearsRef = ref([])
    const editorRef = ref([])

    const input = ref({
        summaryId: "", // 총괄표ID
        summaryName: "", // 총괄표명
        summaryImageName: "", // 총괄표이미지명
        summaryImageSize: "", // 총괄표이미지사이즈
        summaryImageDir: "", // 총괄표이미지위치
        curriculumList: [   // 과정 목록
            {   
                curriculumGb: "01",
                curriculumOrder: "1",
                curriculumFiled: "",
                curriculumName: "",
                curriculumLabel: "",
                curriculumMth: "",
                curriculumDays: "",
                curriculumHours: "",
                curriculumTrgt: "",
                curriculumIntro: "",
                annualSchedule: [],
                errors: {
                    curriculumOrder: '',
                    curriculumFiled: '',
                    curriculumName: '',
                    curriculumLabel: '',
                    curriculumMth: '',
                    curriculumDays: '',
                    curriculumHours: '',
                    curriculumTrgt: '',
                    curriculumIntro: '',
                    annualSchedule: '',
                }
            },
            {   
                curriculumGb: "02",
                curriculumOrder: "1",
                curriculumFiled: "",
                curriculumName: "",
                curriculumLabel: "",
                curriculumMth: "",
                curriculumDays: "",
                curriculumHours: "",
                curriculumTrgt: "",
                curriculumIntro: "",
                annualSchedule: [],
                errors: {
                    curriculumOrder: '',
                    curriculumFiled: '',
                    curriculumName: '',
                    curriculumLabel: '',
                    curriculumMth: '',
                    curriculumDays: '',
                    curriculumHours: '',
                    curriculumTrgt: '',
                    curriculumIntro: '',
                    annualSchedule: '',
                }
            }
        ],
    })

    const inputError = ref({
        summaryName: '',
        summaryImageName: '',
        summaryImageSize: '',
        summaryImageDir: '',
    });

    // 이력서 필수 항목
    const yearsGroup = ref([
      { label:"1월", value: "1" },
      { label:"2월", value: "2" },
      { label:"3월", value: "3" },
      { label:"4월", value: "4" },
      { label:"5월", value: "5" },
      { label:"6월", value: "6" },
      { label:"7월", value: "7" },
      { label:"8월", value: "8" },
      { label:"9월", value: "9" },
      { label:"10월", value: "10" },
      { label:"11월", value: "11" },
      { label:"12월", value: "12" },
    ]);

    onMounted(() => {
        const { id } = route.params;
        if (!id) {
            router.push({ name: "SiteCurriculumManage" });
        } else {
            retrieveCurriculumSummaryDetail(id);
        }
    });

    const retrieveCurriculumSummaryDetail = (summaryId) => {
        loadingStore.loading = true;
        curriculumService.retrieveCurriculumSummaryDetail(summaryId)
            .then( async (res) => {
                loadingStore.loading = false;
                if (res.data.code.toLowerCase() === "success") {
                    const result = res.data.result;
                    input.value.summaryId = result.summary.summaryId;
                    input.value.summaryName = result.summary.summaryName;
                    input.value.summaryImageName = result.summary.summaryImageName;
                    input.value.summaryImageSize = result.summary.summaryImageSize;
                    input.value.summaryImageDir = result.summary.summaryImageDir;
                    input.value.curriculumList = normalizeCurriculumList(result.curriculumList);
                    await initCurriculumRefs();
                }
            }).catch((res) => {
                loadingStore.loading = false;
            });
    };

    const uploadThumbFile = () => {
        const fileInput = document.createElement('input');
        fileInput.type= 'file';
        fileInput.accept = 'image/*';
        fileInput.click();

        fileInput.addEventListener('change', function(event){
            const file = event.target.files[0];
            const fileSize = event.target.files[0].size;
            if(fileSize >= 1024 * 1024 * 200) {
                $alert('최대 200MB까지 첨부 가능합니다.');
                return;
            }

            let paramObj = {
                container: 'public',
                folder: 'curriculum',
                file,
            }
            fileService.uploadFile(paramObj)
                .then((res) => {
                    // console.log(res);
                    if(res.data.code.toLowerCase() === 'success') {
                        const result = res.data.result;
                        // console.log(result);
                        input.value.summaryImageName = result.originalFilename;
                        input.value.summaryImageSize = result.fileSize;
                        input.value.summaryImageDir = result.blobUrl;
                    }
                })
                .catch( (error) =>  {
                    console.log(error);
                });
        });
    }

    const removeThumbFile = () => {
        input.value.summaryImageName = '';
        input.value.summaryImageSize = '';
        input.value.summaryImageDir = '';
    }

    const curriculumRefs = ref([]);

    const scrollToLastAdded = async (curriculumGb) => {
        await nextTick();
        
        const groupItems = input.value.curriculumList.filter(el => el.curriculumGb === curriculumGb);
        const lastIndexInGroup = groupItems.length - 1;

        const basicCount = input.value.curriculumList.filter(el => el.curriculumGb === '01').length;
        const targetIndex = curriculumGb === '01' ? lastIndexInGroup : basicCount + lastIndexInGroup;
        
        const lastItem = curriculumRefs.value[targetIndex];
        if (lastItem) {
            lastItem.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    };

    const update = () => {
        const apiParam = _.cloneDeep(input.value);
        const {
            summaryId,
            summaryName,    
            summaryImageName,
            summaryImageSize,
            summaryImageDir,
        } = apiParam;
        const curriculumListSrc = input.value.curriculumList;

        let isInvalid = false;
        clearInutError();
        renumberOrders();

        if(!summaryImageName) {
            inputError.value.summaryImageName = '총괄표 이미지를 첨부하세요.';
            isInvalid = true;
        }
        if(!summaryName) {
            inputError.value.summaryName = '총괄표명을 입력하세요.';
            isInvalid = true;
        } else if(summaryName.length > 200) {
            inputError.value.summaryName = '총괄표명은 최대 200자까지 입력 가능합니다.';
            isInvalid = true;
        }

        if(!curriculumListSrc || curriculumListSrc.length === 0) {
            inputError.value.curriculumList = '과정정보를 입력하세요.';
            isInvalid = true;
        }

        _.each(curriculumListSrc, (item) => {
            if(item.errors) {
                item.errors.curriculumOrder = '';
                item.errors.curriculumFiled = '';
                item.errors.curriculumName = '';
                item.errors.curriculumLabel = '';
                item.errors.curriculumMth = '';
                item.errors.curriculumDays = '';
                item.errors.curriculumHours = '';
                item.errors.curriculumTrgt = '';
                item.errors.curriculumIntro = '';
                item.errors.annualSchedule = '';
            }

            if(!item.curriculumOrder) {
                item.errors.curriculumOrder = '순서를 입력하세요.';
                isInvalid = true;
            }

            if(!item.curriculumFiled) {
                item.errors.curriculumFiled = '분야를 입력하세요.';
                isInvalid = true;
            } else if($getByteLength(item.curriculumFiled) > 14) {
                item.errors.curriculumFiled = '분야는 한글 기준 최대 7자까지 입력 가능합니다.';
                isInvalid = true;
            }

            if(!item.curriculumName) {
                item.errors.curriculumName = '과정명을 입력하세요.';
                isInvalid = true;
            } else if(item.curriculumName.length > 200) {
                item.errors.curriculumName = '과정명은 최대 200자까지 입력 가능합니다.';
                isInvalid = true;
            }

            if(!item.curriculumMth) {
                item.errors.curriculumMth = '방식을 입력하세요.';
                isInvalid = true;
            } else if($getByteLength(item.curriculumMth) > 10) {
                item.errors.curriculumMth = '방식은 한글 기준 최대 5자까지 입력 가능합니다.';
                isInvalid = true;
            }

            if(!item.curriculumDays) {
                item.errors.curriculumDays = '기간을 입력하세요.';
                isInvalid = true;
            } else if($getByteLength(item.curriculumDays) > 12) {
                item.errors.curriculumDays = '기간은 한글 기준 최대 6자까지 입력 가능합니다.';
                isInvalid = true;
            }

            if(!item.curriculumHours) {
                item.errors.curriculumHours = '시간을 입력하세요.';
                isInvalid = true;
            } else if($getByteLength(item.curriculumHours) > 16) {
                item.errors.curriculumHours = '시간은 한글 기준 최대 8자까지 입력 가능합니다.';
                isInvalid = true;
            }

            if(!item.curriculumTrgt) {
                item.errors.curriculumTrgt = '대상을 입력하세요.';
                isInvalid = true;
            } else if($getByteLength(item.curriculumTrgt) > 24) {
                item.errors.curriculumTrgt = '대상은 한글 기준 최대 12자까지 입력 가능합니다.';
                isInvalid = true;
            }

            if(!item.annualSchedule || item.annualSchedule.length === 0) {
                item.errors.annualSchedule = '연간일정을 입력하세요.';
                isInvalid = true;
            }

            if(!item.curriculumIntro) {
                item.errors.curriculumIntro = '과정소개를 입력하세요.';
                isInvalid = true;
            }
        });

        if(!isInvalid) {
            loadingStore.loading = true;
            const newParam = {
                ...apiParam,
                curriculumList: curriculumListSrc.map((item) => ({
                    ...item,
                    annualSchedule: Array.isArray(item.annualSchedule) ? item.annualSchedule.join(",") : '',
                    errors: undefined,
                })),
            };

            curriculumService.updateCurriculum(newParam)
                .then( async (res) => {
                    loadingStore.loading = false;
                    // console.log(res);
                    if(res.data.code.toLowerCase() === 'success') {
                        await $alert("총괄표를 수정했습니다.");
                        router.push({name: 'SiteCurriculumManage'});
                    } else {
                    }
                }).catch( (res) =>  {
                    // console.log(res);
                    loadingStore.loading = false;
                    $alert("총괄표 수정을 실패했습니다.");
                });
        } else {
            $alert("필수입력 항목을 확인하세요.");
        }
    }

    const addNewCurriculum = async (curriculumGb) => {
        const groupCount = input.value.curriculumList.filter(el => el.curriculumGb === curriculumGb).length;
        const newCurriculum = {
            curriculumGb,
            curriculumOrder: groupCount + 1,
            curriculumFiled: "",
            curriculumName: "",
            curriculumLabel: "",
            curriculumMth: "",
            curriculumDays: "",
            curriculumHours: "",
            curriculumTrgt: "",
            curriculumIntro: "",
            annualSchedule: [1,2,3,4,5,6,7,8,9,10,11,12],
            errors: {
                curriculumOrder: '',
                curriculumFiled: '',
                curriculumName: '',
                curriculumLabel: '',
                curriculumMth: '',
                curriculumDays: '',
                curriculumHours: '',
                curriculumTrgt: '',
                curriculumIntro: '',
                annualSchedule: '',
            }
        }
        input.value.curriculumList.push(newCurriculum);
        renumberOrders(curriculumGb);
        await initCurriculumRefs();
        await scrollToLastAdded(curriculumGb);
    };

    const onInputNumeric = (obj, field, e) => {
        const v = e && e.target && e.target.value ? e.target.value : '';
        obj[field] = v.replace(/[^0-9]/g, '');
    };

    const clearInutError = () => {
        inputError.value = {
            summaryName: '',
            summaryImageName: '',
            summaryImageSize: '',
            summaryImageDir: '',
        };

        _.each(input.value.curriculumList, (item) => {
            if(!item.errors) item.errors = {};
            item.errors.curriculumOrder = '';
            item.errors.curriculumFiled = '';
            item.errors.curriculumName = '';
            item.errors.curriculumLabel = '';
            item.errors.curriculumMth = '';
            item.errors.curriculumDays = '';
            item.errors.curriculumHours = '';
            item.errors.curriculumTrgt = '';
            item.errors.curriculumIntro = '';
            item.errors.annualSchedule = '';
        });
    };

    const getGroupIndices = (gb) => {
        const indices = [];
        for (let i = 0; i < input.value.curriculumList.length; i++) {
            if (input.value.curriculumList[i].curriculumGb === gb) indices.push(i);
        }
        return indices;
    };

    const renumberOrders = (gb) => {
        let order = 1;
        for (const item of input.value.curriculumList) {
            if (item.curriculumGb === gb) {
                item.curriculumOrder = order++;
            }
        }
    };

    const initCurriculumRefs = async () => {
        yearsRef.value = [];
        editorRef.value = [];
        await nextTick();
        const refs = Array.isArray(yearsRef.value) ? yearsRef.value : [yearsRef.value];
        const editorRefs = Array.isArray(editorRef.value) ? editorRef.value : [editorRef.value];
        const renderedItems = [
            ...input.value.curriculumList.filter(el => el.curriculumGb === '01'),
            ...input.value.curriculumList.filter(el => el.curriculumGb === '02')
        ];
        const boundLen = Math.min(renderedItems.length, refs.length, editorRefs.length);
        for (let i = 0; i < boundLen; i++) {
            const r = refs[i];
            const initVals = renderedItems[i]._annualScheduleInit;
            const values = Array.isArray(initVals)
                ? initVals
                : (initVals ? initVals.split(',').map(v => v.trim()).filter(v => v) : (Array.isArray(renderedItems[i].annualSchedule) ? renderedItems[i].annualSchedule : []));
            if (r) {
                if (typeof r.initOptionsWithValues === 'function') {
                    r.initOptionsWithValues(values);
                } else if (r.value && typeof r.value.initOptionsWithValues === 'function') {
                    r.value.initOptionsWithValues(values);
                } else if (typeof r.initOptions === 'function') {
                    r.initOptions();
                } else if (r.value && typeof r.value.initOptions === 'function') {
                    r.value.initOptions();
                }
            }
            renderedItems[i].annualSchedule = values;
            const _editorRef = editorRefs[i];
            if (_editorRef && typeof _editorRef.init === 'function') {
                _editorRef.init(renderedItems[i].curriculumIntro || "");
            }
        }
    };
    const normalizeCurriculumList = (list) => {
        return (list || []).map((item) => {
            const scheduleArr = Array.isArray(item.annualSchedule)
                ? item.annualSchedule
                : (item.annualSchedule ? item.annualSchedule.split(',').map(v => v.trim()).filter(v => v) : []);
            return {
                ...item,
                annualSchedule: [],
                _annualScheduleInit: scheduleArr,
                errors: item.errors || {
                    curriculumOrder: '',
                    curriculumFiled: '',
                    curriculumName: '',
                    curriculumLabel: '',
                    curriculumMth: '',
                    curriculumDays: '',
                    curriculumHours: '',
                    curriculumTrgt: '',
                    curriculumIntro: '',
                    annualSchedule: '',
                },
            };
        });
    };

    const copyCurriculum = async (index, gb) => {
        const groupItems = input.value.curriculumList.filter(el => el.curriculumGb === gb);
        const target = groupItems[index];
        if (!target) return;
        const copyTarget = _.cloneDeep(target);
        const copyItem = {
            ...copyTarget,
            curriculumId: new Date().getTime(),
        };
        input.value.curriculumList.push(copyItem);
        await initCurriculumRefs();
        renumberOrders(gb);
        await scrollToLastAdded(gb);
    };

    const moveUpCurriculum = async (index, gb) => {
        const groupIndices = getGroupIndices(gb);
        if (index <= 0 || index >= groupIndices.length) return;
        const a = groupIndices[index];
        const b = groupIndices[index - 1];
        const list = input.value.curriculumList;
        const temp = list[b];
        list[b] = list[a];
        list[a] = temp;
        renumberOrders(gb);
        await initCurriculumRefs();
    };

    const moveDownCurriculum = async (index, gb) => {
        const groupIndices = getGroupIndices(gb);
        if (index < 0 || index >= groupIndices.length - 1) return;
        const a = groupIndices[index];
        const b = groupIndices[index + 1];
        const list = input.value.curriculumList;
        const temp = list[b];
        list[b] = list[a];
        list[a] = temp;
        renumberOrders(gb);
        await initCurriculumRefs();
    };

    const deleteCurriculum = async (index, gb) => {
        const groupIndices = getGroupIndices(gb);
        if(input.value.curriculumList.filter(el => el.curriculumGb === gb).length === 1) {
            const allMonths = yearsGroup.value.map(o => o.value);
            const target = input.value.curriculumList[groupIndices[index]];
            input.value.curriculumList[groupIndices[index]] = {
                ...target,
                curriculumOrder: '',
                curriculumFiled: '',
                curriculumName: '',
                curriculumLabel: '',
                curriculumMth: '',
                curriculumDays: '',
                curriculumHours: '',
                curriculumTrgt: '',
                curriculumIntro: '',
                annualSchedule: allMonths,
                errors: {
                    curriculumOrder: '',
                    curriculumFiled: '',
                    curriculumName: '',
                    curriculumLabel: '',
                    curriculumMth: '',
                    curriculumDays: '',
                    curriculumHours: '',
                    curriculumTrgt: '',
                    curriculumIntro: '',
                    annualSchedule: '',
                }
            }

            const refs = Array.isArray(yearsRef.value) ? yearsRef.value : [yearsRef.value];
            const basicCount = input.value.curriculumList.filter(el => el.curriculumGb === '01').length;
            const refIndex = gb === '01' ? index : basicCount + index;
            const r = refs[refIndex];
            if (r) {
                if (typeof r.initOptionsWithValues === 'function') {
                    r.initOptionsWithValues(allMonths);
                } else if (r.value && typeof r.value.initOptionsWithValues === 'function') {
                    r.value.initOptionsWithValues(allMonths);
                }
            }

            const editorRefs = Array.isArray(editorRef.value) ? editorRef.value : [editorRef.value];
            const _editorRef = editorRefs[refIndex];
            if (_editorRef) {
                if (typeof _editorRef.init === 'function') {
                    _editorRef.init("");
                }
            }

            return;
        }

        const ok = await $confirm(["작성한 정보를 삭제할까요?"], "삭제");
        if (!ok) return;
        if (index < 0 || index >= groupIndices.length) return;
        input.value.curriculumList.splice(groupIndices[index], 1);

        input.value.curriculumList = normalizeCurriculumList(_.cloneDeep(input.value.curriculumList));
        await initCurriculumRefs();

        renumberOrders(gb);
    };

    const isDisabled = (index) => {
        return false;
    };

    const isCreateDisabled = computed(() => {
        const { summaryName, summaryImageName } = input.value;
        if(!summaryImageName || !summaryName) return true;
        const list = input.value.curriculumList;
        if(!list || list.length === 0) return true;
        for(const item of list) {
            if(!item.curriculumOrder) return true;
            if(!item.curriculumFiled) return true;
            if(!item.curriculumName) return true;
            if(!item.curriculumMth) return true;
            if(!item.curriculumDays) return true;
            if(!item.curriculumHours) return true;
            if(!item.curriculumTrgt) return true;
            if(!item.curriculumIntro) return true;
            if(!item.annualSchedule || (Array.isArray(item.annualSchedule) ? item.annualSchedule.length === 0 : item.annualSchedule === '')) return true;
        }
        return false;
    });
</script>

<template>
    <div class="content">
        <div class="sub-title">
            <h3>총괄표 수정</h3>
            <p><span>* 표시는 필수 입력사항</span> 입니다.</p>
        </div>
        <div class="table-form content-inner">
            <!-- s: 총괄표 정보 -->
            <div class="table-title text-right">
                <h4>● 총괄표 정보</h4>
            </div>
            <table class="vertical">
                <colgroup>
                    <col width="200px" />
                    <col />
                </colgroup>
                <tr>
                    <th class="mark">총괄표명</th>
                    <td>
                        <KSAInput class="text-box" v-model="input.summaryName" :msg="inputError.summaryName" max-length="200" placeholder="총괄표명을 입력해주세요.(최대 200자 입력가능)"/>
                    </td>
                </tr>
                <tr>
                    <th><span class="mark">총괄표 이미지</span><br><span>(다운로드 파일)</span></th>
                    <td class="text-box">
                        <div class="grid file">
                            <KSAPrimaryButton text="파일 선택" @click="uploadThumbFile()" />
                            <KSAInput class="input icon" placeholder="파일 선택" v-model="input.summaryImageName" disabled />
                            <button class="delete" :disabled="!input.summaryImageName" @click="removeThumbFile()"><i></i></button>
                        </div>
                        <small>최대 200MB 까지 첨부 가능</small>
                        <div>
                            <small style="color:#E71414;">{{ inputError.summaryImageName }}</small>
                        </div>
                    </td>
                </tr>
            </table>
            <!-- e: 총괄표 정보 -->
            <!-- s: 과정 정보 - 기초 -->
            <div class="table-title text-right">
                <h4>● 과정 정보 - 기초 ({{ input.curriculumList.filter(el => el.curriculumGb === '01').length }})</h4>
                <KSASecondaryButton class="gray_button add_btn" text="추가" @click="addNewCurriculum('01')" />
            </div>
            <table class="content-item vertical" v-for="(c, index) in input.curriculumList.filter(el => el.curriculumGb === '01')" :key="index" :ref="el => { if (el) curriculumRefs[index] = el }">
                <colgroup>
                    <col width="10%" />
                    <col width="40%"/>
                    <col width="10%" />
                    <col width="40%"/>
                </colgroup>
                <tr>
                    <th colspan="4">
                        <div class="curriculum-title text-right">
                            <span>과정</span>
                            <div class="control pos-none">
                                <KSASecondaryButton class="gray_button" text="과정 복사" @click="copyCurriculum(index, '01')" :disabled="isDisabled(index)"/>
                                <div class="up" :class="{'active': index > 0 && input.curriculumList.filter(el => el.curriculumGb === '01').length > 1 }" @click="moveUpCurriculum(index, '01')"><i></i></div>
                                <div class="down" :class="{'active': index < input.curriculumList.filter(el => el.curriculumGb === '01').length-1 && input.curriculumList.filter(el => el.curriculumGb === '01').length > 1 }" @click="moveDownCurriculum(index, '01')"><i></i></div>
                                <div class="delete active" @click="deleteCurriculum(index, '01')"><i></i></div>
                            </div>
                        </div>
                    </th>
                </tr>
                <tr>
                    <th>순서</th>
                    <td>
                        <KSAInput class="text-box" v-model="c.curriculumOrder" @input="(e)=> onInputNumeric(c,'curriculumOrder',e)" :msg="inputError.curriculumOrder" max-length="3" placeholder="숫자로만 입력" disabled />
                    </td>
                    <th class="mark">분야</th>
                    <td>
                        <KSAInputWithBytes class="text-box" v-model="c.curriculumFiled" :msg="c.errors && c.errors.curriculumFiled" max-bytes="14" placeholder="ex) 산업공통, 장비부품, 테스트, 패키징 등"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">과정명</th>
                    <td colspan="3">
                        <KSAInput class="text-box" v-model="c.curriculumName" :msg="c.errors && c.errors.curriculumName" max-length="200" placeholder="과정명을 입력해주세요.(최대 200자 입력가능)"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">라벨</th>
                    <td>
                        <KSARadioButton class="mr-md" :name="`curriculumLabel01_${index}`" label="없음" v-model="c.curriculumLabel" value="" />
                        <KSARadioButton class="mr-md" :name="`curriculumLabel01_${index}`" label="<span class='label blue'>취업중점</span>" v-model="c.curriculumLabel" value="취업중점" />
                        <KSARadioButton class="mr-md" :name="`curriculumLabel01_${index}`" label="<span class='label blue_line'>채용연계</span>" v-model="c.curriculumLabel" value="채용연계" />
                    </td>
                    <th class="mark">방식</th>
                    <td>
                        <KSAInputWithBytes class="text-box" v-model="c.curriculumMth" :msg="c.errors && c.errors.curriculumMth" max-bytes="10" placeholder="ex) 온라인, 이론, 이론/실습 등​"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">교육 일수</th>
                    <td class="align-center">
                        <KSAInputWithBytes style="width: 160px; margin-bottom: 0;" class="text-box" v-model="c.curriculumDays" max-bytes="12" placeholder="ex) - , 7일, 2~3일"/>기간(일)
                        <KSAInputWithBytes style="width: 160px; margin-bottom: 0;" class="text-box" v-model="c.curriculumHours" max-bytes="16" placeholder="ex) 50M, 3H, 24H"/>시간
                        <div>
                            <small style="color:#E71414;">{{ c.errors && c.errors.curriculumDays }} {{ c.errors && c.errors.curriculumHours }}</small>
                        </div>
                    </td>
                    <th class="mark">대상</th>
                    <td>
                        <KSAInputWithBytes class="text-box" v-model="c.curriculumTrgt" :msg="c.errors && c.errors.curriculumTrgt" max-bytes="24" placeholder="ex) 모두, 미취업자, 재직자 등"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">연간 교육 일정</th>
                    <td colspan="3">
                        <div style="display: inline-block;">
                            <KSACheckBoxGroup
                                    ref="yearsRef"
                                    className="mr-md"
                                    name="yearsList"
                                    v-model="c.annualSchedule"
                                    :options="yearsGroup"
                                    :allUnchecked="c.curriculumId ? true : false"
                                />
                        </div>
                        <div>
                            <small style="color:#E71414;">{{ c.errors && c.errors.annualSchedule }}</small>
                        </div>
                    </td>
                </tr>
                <tr>
                    <th class="mark">과정 소개</th>
                    <td colspan="3">
                        <KSACKEditor ref="editorRef" v-model="c.curriculumIntro" container="public"/>
                        <div style="display:flex; justify-content: space-between;">
                            <small style="color:#E71414;">{{ c.errors && c.errors.curriculumIntro }}</small>
                            <!-- <span><span :class="{'red': c.curriculumIntro.length > 5000}">{{ $number(c.curriculumIntro.length) }}</span>/5,000자</span> -->
                        </div>
                    </td>
                </tr>
            </table>
            <!-- e: 과정 정보 - 기초 -->
            <!-- s: 과정 정보 - 심화 -->
            <div class="table-title text-right">
                <h4>● 과정 정보 - 심화 ({{ input.curriculumList.filter(el => el.curriculumGb === '02').length }})</h4>
                <KSASecondaryButton class="gray_button add_btn" text="추가" @click="addNewCurriculum('02')" />
            </div>
            <table class="content-item vertical" v-for="(c, index) in input.curriculumList.filter(el => el.curriculumGb === '02')" :key="index" :ref="el => { if (el) curriculumRefs[input.curriculumList.filter(i => i.curriculumGb === '01').length + index] = el }">
                <colgroup>
                    <col width="10%" />
                    <col width="40%"/>
                    <col width="10%" />
                    <col width="40%"/>
                </colgroup>
                <tr>
                    <th colspan="4">
                        <div class="curriculum-title text-right">
                            <span>과정</span>
                            <div class="control pos-none">
                                <KSASecondaryButton class="gray_button" text="과정 복사" @click="copyCurriculum(index, '02')" :disabled="isDisabled(index)"/>
                                <div class="up" :class="{'active': index > 0 && input.curriculumList.filter(el => el.curriculumGb === '02').length > 1 }" @click="moveUpCurriculum(index, '02')"><i></i></div>
                                <div class="down" :class="{'active': index < input.curriculumList.filter(el => el.curriculumGb === '02').length-1 && input.curriculumList.filter(el => el.curriculumGb === '02').length > 1 }" @click="moveDownCurriculum(index, '02')"><i></i></div>
                                <div class="delete active" @click="deleteCurriculum(index, '02')"><i></i></div>
                            </div>
                        </div>
                    </th>
                </tr>
                <tr>
                    <th>순서</th>
                    <td>
                        <KSAInput class="text-box" v-model="c.curriculumOrder" @input="(e)=> onInputNumeric(c,'curriculumOrder',e)" :msg="inputError.curriculumOrder" max-length="3" placeholder="숫자로만 입력" disabled />
                    </td>
                    <th class="mark">분야</th>
                    <td>
                        <KSAInputWithBytes class="text-box" v-model="c.curriculumFiled" :msg="c.errors && c.errors.curriculumFiled" max-bytes="14" placeholder="ex) 산업공통, 장비부품, 테스트, 패키징 등"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">과정명</th>
                    <td colspan="3">
                        <KSAInput class="text-box" v-model="c.curriculumName" :msg="c.errors && c.errors.curriculumName" max-length="200" placeholder="과정명을 입력해주세요.(최대 200자 입력가능)"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">라벨</th>
                    <td>
                        <KSARadioButton class="mr-md" :name="`curriculumLabel02_${index}`" label="없음" v-model="c.curriculumLabel" value="" />
                        <KSARadioButton class="mr-md" :name="`curriculumLabel02_${index}`" label="<span class='label blue'>취업중점</span>" v-model="c.curriculumLabel" value="취업중점" />
                        <KSARadioButton class="mr-md" :name="`curriculumLabel02_${index}`" label="<span class='label blue_line'>채용연계</span>" v-model="c.curriculumLabel" value="채용연계" /> 
                    </td>
                    <th class="mark">방식</th>
                    <td>
                        <KSAInputWithBytes class="text-box" v-model="c.curriculumMth" :msg="c.errors && c.errors.curriculumMth" max-bytes="10" placeholder="ex) 온라인, 이론, 이론/실습 등​"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">교육 일수</th>
                    <td class="align-center">
                        <KSAInputWithBytes style="width: 160px; margin-bottom: 0;" class="text-box" v-model="c.curriculumDays" max-bytes="12" placeholder="ex) - , 7일, 2~3일"/>기간(일)
                        <KSAInputWithBytes style="width: 160px; margin-bottom: 0;" class="text-box" v-model="c.curriculumHours" max-bytes="16" placeholder="ex) 50M, 3H, 24H"/>시간
                        <div style="display: block;">
                            <small style="color:#E71414;">{{ c.errors && c.errors.curriculumDays }} {{ c.errors && c.errors.curriculumHours }}</small>
                        </div>
                    </td>
                    <th class="mark">대상</th>
                    <td>
                        <KSAInputWithBytes class="text-box" v-model="c.curriculumTrgt" :msg="c.errors && c.errors.curriculumTrgt" max-bytes="24" placeholder="ex) 모두, 미취업자, 재직자 등"/>
                    </td>
                </tr>
                <tr>
                    <th class="mark">연간 교육 일정</th>
                    <td colspan="3">
                        <div style="display: inline-block;">
                            <KSACheckBoxGroup
                                ref="yearsRef"
                                className="mr-md"
                                name="yearsList"
                                v-model="c.annualSchedule"
                                :options="yearsGroup"
                                :allUnchecked="c.curriculumId ? true : false"
                            />
                        </div>
                        <div>
                            <small style="color:#E71414;">{{ c.errors && c.errors.annualSchedule }}</small>
                        </div>
                    </td>
                </tr>
                <tr>
                    <th class="mark">과정 소개</th>
                    <td colspan="3">
                        <KSACKEditor ref="editorRef" v-model="c.curriculumIntro" container="public"/>
                        <div style="display: flex; justify-content: space-between;">
                            <small style="color:#E71414;">{{ c.errors && c.errors.curriculumIntro }}</small>
                            <!-- <span><span :class="{'red': c.curriculumIntro.length > 5000}">{{ $number(c.curriculumIntro.length) }}</span>/5,000자</span> -->
                        </div>
                    </td>
                </tr>
            </table>
            <!-- e: 과정 정보 - 심화 -->
        </div>
        <div class="btn-wrap space-between">
            <KSASecondaryButton class="gray_button" text="취소" @click="router.push({name: 'SiteCurriculumDetail', params: { id: route.params.id }})" />
            <KSAPrimaryButton text="수정" @click="update" :disabled="isCreateDisabled" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "../../../../assets/scss/theme";

    table {
        margin-bottom: 30px;
    }

    .table-title {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-bottom: 20px;
    }

    .curriculum-title {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .pos-none {
        position: relative !important;
        top:0 !important;
        right: 0 !important;
    }
</style>
